import * as React from 'react';

import type { KbProfile, KbThreadSummaryDto } from '@/api/kb-client';
import { Badge } from '@/components/ui/badge';
import { formatDateTime } from '@/routes/communication/chat-format';
import { KbSnippet } from '@/routes/communication/kb-snippet';
import { cn } from '@/lib/utils';

// =============================================================================
// kb-thread-list.tsx — Panneau gauche : liste des fils ARCHIVÉS (R15-COM-04).
// =============================================================================
// Liste des fils clôturés (tri = autorité API). Chaque fil est un BOUTON natif (navigation clavier
// native, WCAG 2.1.1) ; le fil ouvert porte `aria-current="true"`. Le SNIPPET FTS (si recherche) est rendu
// via KbSnippet (segments ÉCHAPPÉS + `<mark>` — JAMAIS d'HTML brut). Les tags (admin) portent un LIBELLÉ
// TEXTE. Le nombre de messages est un compteur textuel. Aucune information portée par la seule couleur.

export interface KbThreadListProps {
  readonly profile: KbProfile;
  readonly threads: readonly KbThreadSummaryDto[];
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string) => void;
  /** État de chargement (statut accessible). */
  readonly isLoading: boolean;
}

export function KbThreadList({
  profile,
  threads,
  selectedThreadId,
  onSelectThread,
  isLoading,
}: KbThreadListProps): React.JSX.Element {
  if (isLoading) {
    return (
      <p role="status" className="p-4 text-sm text-muted-foreground">
        Chargement des conversations archivées…
      </p>
    );
  }

  if (threads.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        Aucune conversation archivée à afficher.
      </p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-border">
      {threads.map((thread) => {
        const isSelected = thread.threadId === selectedThreadId;
        return (
          <li key={thread.threadId}>
            <button
              type="button"
              aria-current={isSelected ? true : undefined}
              onClick={() => onSelectThread(thread.threadId)}
              className={cn(
                'flex w-full flex-col gap-1 px-4 py-3 text-left text-sm',
                'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                isSelected ? 'bg-accent text-accent-foreground' : '',
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="min-w-0 truncate font-medium">
                  {profile === 'admin' ? thread.supplierSiret : 'JeKo'}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {thread.messageCount} message
                  {thread.messageCount > 1 ? 's' : ''}
                </span>
              </span>
              <span className="truncate text-xs text-muted-foreground">
                Campagne {thread.campaignId}
              </span>
              {thread.snippet !== null ? (
                <KbSnippet
                  snippet={thread.snippet}
                  className="line-clamp-2 text-xs text-foreground"
                />
              ) : null}
              <span className="text-[11px] text-muted-foreground">
                Archivé le {formatDateTime(thread.archivedAt)}
              </span>
              {thread.tags.length > 0 ? (
                <span className="mt-1 flex flex-wrap gap-1">
                  {thread.tags.map((tag) => (
                    <Badge key={tag.tagId} variant="outline">
                      {tag.label}
                    </Badge>
                  ))}
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
